import React from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Search,
  Add,
  MoreVert,
  Badge,
  Email,
  Phone,
  Edit,
  Delete,
  Visibility,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ManageStaffDialog from '../components/ManageStaffDialog';

const mockStaff = [
  {
    id: 'S001',
    name: 'Michael Green',
    role: 'Manager',
    department: 'Management',
    email: 'michael@hotelops.com',
    phone: '555-100-1001',
    salary: 5500,
    status: 'Active',
    joinDate: '2022-01-15',
    shift: 'Day',
  },
  {
    id: 'S002',
    name: 'Sara White',
    role: 'Receptionist',
    department: 'Front Desk',
    email: 'sara@hotelops.com',
    phone: '555-100-1002',
    salary: 2800,
    status: 'Active',
    joinDate: '2023-03-20',
    shift: 'Day',
  },
  {
    id: 'S003',
    name: 'Tom Black',
    role: 'Housekeeping',
    department: 'Housekeeping',
    email: 'tom@hotelops.com',
    phone: '555-100-1003',
    salary: 2200,
    status: 'On Leave',
    joinDate: '2023-06-10',
    shift: 'Day',
  },
  {
    id: 'S004',
    name: 'Lisa Brown',
    role: 'Chef',
    department: 'Kitchen',
    email: 'lisa@hotelops.com',
    phone: '555-100-1004',
    salary: 3500,
    status: 'Active',
    joinDate: '2022-08-05',
    shift: 'Day',
  },
  {
    id: 'S005',
    name: 'James Wilson',
    role: 'Security',
    department: 'Security',
    email: 'james@hotelops.com',
    phone: '555-100-1005',
    salary: 2600,
    status: 'Active',
    joinDate: '2023-11-12',
    shift: 'Night',
  },
  {
    id: 'S006',
    name: 'Emma Davis',
    role: 'Receptionist',
    department: 'Front Desk',
    email: 'emma@hotelops.com',
    phone: '555-100-1006',
    salary: 2800,
    status: 'Active',
    joinDate: '2024-02-18',
    shift: 'Night',
  },
];

export default function Staff() {
  const navigate = useNavigate();
  const [staff, setStaff] = React.useState(mockStaff);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedStaff, setSelectedStaff] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [manageDialogOpen, setManageDialogOpen] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState('add');
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

  const filteredStaff = staff.filter((member) => {
    const query = searchQuery.toLowerCase();
    return (
      member.name.toLowerCase().includes(query) ||
      member.role.toLowerCase().includes(query) ||
      member.department.toLowerCase().includes(query) ||
      member.id.toLowerCase().includes(query)
    );
  });

  const handleMenuClick = (event, staffMember) => {
    setAnchorEl(event.currentTarget);
    setSelectedStaff(staffMember);
  };

  const handleAddStaff = () => {
    setSelectedStaff(null);
    setDialogMode('add');
    setManageDialogOpen(true);
  };

  const handleEditStaff = () => {
    setDialogMode('edit');
    setManageDialogOpen(true);
    setAnchorEl(null);
  };

  const handleViewDetails = () => {
    if (selectedStaff) {
      navigate(`/staff/${selectedStaff.id}`);
    }
    setAnchorEl(null);
  };

  const handleSaveStaff = (staffData) => {
    if (dialogMode === 'add') {
      setStaff([...staff, staffData]);
      setSnackbar({
        open: true,
        message: `Staff member ${staffData.name} added successfully!`,
        severity: 'success',
      });
    } else {
      setStaff(staff.map(s => s.id === staffData.id ? staffData : s));
      setSnackbar({
        open: true,
        message: `Staff member ${staffData.name} updated successfully!`,
        severity: 'success',
      });
    }
  };

  const handleDeleteStaff = () => {
    if (selectedStaff) {
      setStaff(staff.filter(s => s.id !== selectedStaff.id));
      setSnackbar({
        open: true,
        message: `Staff member ${selectedStaff.name} removed successfully!`,
        severity: 'success',
      });
      setAnchorEl(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'On Leave': return 'warning';
      case 'Inactive': return 'default';
      default: return 'default';
    }
  };

  const stats = [
    { label: 'Total Staff', value: staff.length, color: '#1976d2' },
    { label: 'Active Staff', value: staff.filter(s => s.status === 'Active').length, color: '#2e7d32' },
    { label: 'On Leave', value: staff.filter(s => s.status === 'On Leave').length, color: '#ed6c02' },
    { 
      label: 'Monthly Payroll', 
      value: `$${staff.reduce((sum, s) => sum + s.salary, 0).toLocaleString()}`, 
      color: '#9c27b0' 
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Staff Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage hotel staff and employees
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleAddStaff}>
          Add Staff Member
        </Button>
      </Box>

      <Grid container spacing={3} mb={3}>
        {stats.map((stat) => (
          <Grid item xs={6} md={3} key={stat.label}>
            <Card sx={{ bgcolor: `${stat.color}10`, borderLeft: `4px solid ${stat.color}` }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                <Typography variant="h4" fontWeight="bold" color={stat.color}>
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ mb: 3, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search staff by name, role, department, or ID..."
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Staff ID</strong></TableCell>
              <TableCell><strong>Employee Details</strong></TableCell>
              <TableCell><strong>Department</strong></TableCell>
              <TableCell><strong>Contact</strong></TableCell>
              <TableCell><strong>Shift</strong></TableCell>
              <TableCell><strong>Salary</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStaff.map((member) => (
              <TableRow key={member.id} hover>
                <TableCell><strong>{member.id}</strong></TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ bgcolor: '#1976d2' }}>
                      {member.name[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {member.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {member.role}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{member.department}</TableCell>
                <TableCell>
                  <Box>
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="caption">{member.email}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="caption">{member.phone}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={member.shift} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <strong>${member.salary.toLocaleString()}</strong>
                </TableCell>
                <TableCell>
                  <Chip
                    label={member.status}
                    size="small"
                    color={getStatusColor(member.status)}
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={(e) => handleMenuClick(e, member)}>
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleEditStaff}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit Staff
        </MenuItem>
        <MenuItem onClick={handleViewDetails}>
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleDeleteStaff}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Remove Staff
        </MenuItem>
      </Menu>

      <ManageStaffDialog
        open={manageDialogOpen}
        onClose={() => setManageDialogOpen(false)}
        staff={selectedStaff}
        onSave={handleSaveStaff}
        mode={dialogMode}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
